import { Injectable } from '@angular/core';
import { WebsocketService } from "./websocket.service";
import {Store} from "@ngrx/store";
import {Socket} from "socket.io-client";
import {DefaultEventsMap} from "@socket.io/component-emitter";
import {User} from "../auth/primitives/user";
import * as AuthSelectors from '../state/auth/auth.selector';
import * as UserSelectors from '../state/users/users.selector';
import {authActions} from "../state/auth/auth.actions";
import {selectAuthIsReady} from "../state/auth/auth.selector";
import {IHasSocket} from "./i-has-socket";
import {UsersActions} from "../state/users/usersActions";
import * as networkSelectors from "../state/network/network.selector";
import {selectWorldReady} from "../state/world/world.selector";
import {Vector3} from "three";
import {selectCameraTarget} from "../state/camera/camera.selector";
import {auditTime} from "rxjs";
import {DoWhenReadyService, Readyables} from "./do-when-ready.service";

@Injectable({
  providedIn: 'root'
})
export class UsersService implements IHasSocket {
  namespace = "users"
  socket: Socket<DefaultEventsMap, DefaultEventsMap>
  user: User = null

  subscribedUserIds: string[] = []
  knownUserIds: string[] = []
  cameraTarget: Vector3 = null

  constructor(
      private websocketService: WebsocketService,
      private store: Store,
      private doWhenReady: DoWhenReadyService,
  ) {
    this.websocketService.usersSocket.subscribe(async (socket) => {
      if (!socket) return
      this.socket = socket
      this.socket.connect()
      this.setupActionHandlers()
    })
    this.doWhenReady.everyTime(this, [Readyables.auth], async (user) => {
      this.user = user
      if (user && !this.subscribedUserIds.includes(user.id)) {
        store.dispatch(
            UsersActions.subscribeToUser({userId: this.user.id})
        )
      }
      this.store.select(UserSelectors.selectIds)
          .subscribe((ids) => {
            if (!ids) return
            this.knownUserIds = ids.map(id => id.toString())
          })
      this.store.select(UserSelectors.selectSubscribedToUserIds)
          .subscribe(async (ids) => {
            if (!ids) return
            this.subscribedUserIds = ids.map(id => id.toString())
            await this.updateUserSubscriptions()
          })
    })
    this.store.select(selectCameraTarget).pipe(
        auditTime(1000)
    ).subscribe(async (target) => {
      if (target && this.socket?.connected) {
        await this.setMyLastPosition(target)
      }
    })
  }

  async emitWithAck(endpoint: string, data: any) {
    try {
      const response: string = await this.socket
          .emitWithAck(endpoint, data);
      return response
    } catch (e) {
      console.error(e)
      // throw e
      return null
    }
  }

  async updateUserSubscriptions() {
    const response: string = await this.emitWithAck("update_user_subscriptions", {
      userIds: this.subscribedUserIds,
    });
  }

  setupActionHandlers() {
    this.socket.on("UserUpdate", (jsonData) => {
      let data = jsonData.user;
      let user: User = JSON.parse(data);
      if (user.id === this.user.id) {
        this.store.dispatch(authActions.meUpdated({
          user: user
        }))
      }
      if (this.knownUserIds.includes(user.id)) {
        this.store.dispatch(UsersActions.updatedUser({
          user: user
        }))
      } else {
        this.store.dispatch(UsersActions.foundUser({
          user: user
        }))
      }
    });
  }

  async updateUser({displayName, tagLine, profileImageId}) {
    const updatedUserString = await this.socket
        .emitWithAck("update_me", {
      displayName: displayName,
      tagLine: tagLine,
      profileImageId: profileImageId,
    })
    const updatedUser = JSON.parse(updatedUserString) as User
    this.store.dispatch(authActions.meUpdated({
      user: updatedUser
    }))
    this.store.dispatch(UsersActions.updatedUser({user: updatedUser}))
  }

  async getUser(userId: string): Promise<User>{
    const response: string = await this.socket
        .emitWithAck("get_user", {
          userId: userId,
        });
    // console.log(response)
    const user = JSON.parse(response)
    return user
  }

  async _getUser(userId: string) {
    const response: string = await this.socket
        .emitWithAck("get_user", {
      userId: userId,
    });
    // console.log(response)
    const user = JSON.parse(response)
    this.store.dispatch(UsersActions.foundUser({user: user}))
  }

  async setMyLastPosition(target: Vector3) {
    const response: string = await this.socket
        .emitWithAck("set_my_last_position", {
          target: target,
    });
  }
}
